import {
  APIProvider,
  InfoWindow,
  Map,
  Marker,
} from "@vis.gl/react-google-maps";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "reactstrap";

import SharedViewRecordDrawer from "../PageView/PageViewTable/SharedToolbar/SharedViewRecordDrawer";
import { DetailView } from "../PageView/PageViewTable/SharedToolbar/SharedToolbar";
import FieldFormatter from "../Records/Formatters/FieldFormatter";
import AppTablePagination from "../AppTable/AppTablePagination";
import { isValidMapQuery } from "../../utils/DashboardUtils";
import RecordNameCell from "../Records/RecordNameCell";
import { Module } from "../../types/TenantInterfaces";
import useFieldMap from "../../hooks/useFieldMap";
import useMapData from "./WidgetHooks/useMapData";
import Constants from "../../utils/Constants";
import useModule from "../../hooks/useModule";
import { SelectedChart } from "./WidgetTypes";
import WidgetLoading from "./WidgetLoading";
import WidgetError from "./WidgetError";

interface MapWidgetProps {
  section: any;
  setSettings: any;
  theme?: string[];
  topLevelFilters?: any;
  viewFilters?: any;
  record: string;
  onSelectGroup?: (chartGroup: SelectedChart | undefined) => void;
  selectedGroup?: SelectedChart;
}

/**
 *
 */
const MapWidget = ({
  section,
  topLevelFilters,
  setSettings,
  viewFilters,
  record,
  onSelectGroup,
}: MapWidgetProps) => {
  const { module } = useModule(section?.ModuleAPIName);
  const fieldTypeMap = useFieldMap(section?.ModuleAPIName);
  const [openRecordData, setOpenRecordData] = useState<DetailView>(null);
  const [query, setQuery] = useState(section?.Query);
  const [index, setIndex] = useState(-1);

  useEffect(() => {
    setQuery(section?.Query);
  }, [section]);

  const { mapData, pagination, mapDataLoading, mapError, countFieldName } =
    useMapData({
      moduleAPIName: section?.ModuleAPIName,
      record: record,
      query: { ...query },
      validator: isValidMapQuery,
      topLevelFilters: topLevelFilters,
      viewFilters: viewFilters,
      locationField: section?.LocationField,
      queryKeyID: section?.id,
    });

  const imageField = useMemo(() => {
    return (module as Module)?.Templates?.RecordDetailView?.ImageField || "";
  }, [module]);

  const fields = useMemo(() => {
    if (index < 0) return [];

    const fieldAPINames = section?.Query?.Fields?.filter((f: any) => f !== "*");
    const selectedMarker = mapData[index];

    const fieldFormatterArray: any[] = [];

    for (const fieldAPIName of fieldAPINames) {
      const field = fieldTypeMap[fieldAPIName];

      if (!field || !selectedMarker) continue;

      fieldFormatterArray.push(
        <FieldFormatter
          key={`${fieldAPIName}-${selectedMarker?.id}`}
          field={field}
          value={selectedMarker[fieldAPIName]}
          record={selectedMarker}
          recordID={selectedMarker?.id}
          moduleAPIName={selectedMarker?._ModuleAPIName}
          forceReadOnly={false}
          subitemParentID={null}
          multiSubitemID={null}
          isDrawer={false}
          createMode={false}
          table={false}
        />
      );
    }

    return fieldFormatterArray;
  }, [section?.Query?.Fields, mapData, index, fieldTypeMap]);

  if (mapError) {
    return (
      <WidgetError
        onOpenSettings={setSettings ? () => setSettings(section) : null}
      />
    );
  }

  if (mapDataLoading) {
    return <WidgetLoading />;
  }

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <APIProvider apiKey={Constants.GOOGLE_MAPS_KEY}>
        <Map
          gestureHandling={"auto"}
          defaultZoom={3}
          defaultCenter={{
            lat: 37.0902,
            lng: -95.7129,
          }}
          disableDefaultUI={true}
        >
          {mapData.map((r, index) => {
            return (
              !!r._coordinates && (
                <Marker
                  key={r.id}
                  position={{
                    lat: r._coordinates?.lat,
                    lng: r._coordinates?.lng,
                  }}
                  optimized={true}
                  onClick={(e) => {
                    e.stop();
                    setIndex(index);
                    onSelectGroup &&
                      onSelectGroup({
                        group: [
                          {
                            Group: false,
                            groupByField: "_id",
                            groupByValue: r?.id,
                          },
                        ],
                      });
                  }}
                />
              )
            );
          })}
          {index >= 0 && (
            <InfoWindow
              style={{ fontFamily: "poppins" }}
              position={{
                lat: mapData[index]?._coordinates?.lat,
                lng: mapData[index]?._coordinates?.lng,
              }}
              onClose={() => {
                setIndex(-1);
                onSelectGroup && onSelectGroup(undefined);
              }}
              headerContent={
                <RecordNameCell
                  record={mapData[index]}
                  onOpen={(module, record) =>
                    setOpenRecordData({
                      recordID: record?._id || record?.id,
                      moduleAPIName: module?.APIName,
                      tab: "Summary",
                      viewID: "",
                    })
                  }
                  disableViewRecord={false}
                  imageField={imageField}
                  imageFieldPresentation={null}
                  metadataField={null}
                  metadataFieldType={null}
                />
              }
            >
              {!!fields?.length && fields?.map((f: any) => f)}
              {countFieldName && (
                <Badge color="primary">
                  {/* @ts-ignore */}
                  {mapData[index]?.[countFieldName]} -{" "}
                  {section?.PinnedModuleAPIName}
                </Badge>
              )}
            </InfoWindow>
          )}
        </Map>
      </APIProvider>
      <SharedViewRecordDrawer
        openRecordData={openRecordData}
        setOpenRecordData={setOpenRecordData}
        // @ts-ignore
        view={null}
      />
      <AppTablePagination
        records={mapData}
        pagination={pagination}
        query={query}
        setQuery={setQuery}
        error={mapError}
        modifyPaginationCount={false}
      />
    </div>
  );
};

export default MapWidget;
